import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Loader2,
  RotateCcw,
  Unplug,
} from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { connectCalendarUrl, disconnectCalendar } from "./calendarApi";
import { CALENDAR_PROVIDERS, calendarProviderName } from "./calendarProviders";
import { CalendarConnection, CalendarKind, WritableCalendar } from "./types";

export type CalendarsState =
  | { kind: "loading" }
  | { kind: "ok"; calendars: WritableCalendar[] }
  | { kind: "none" }
  | { kind: "reconnect" }
  | { kind: "error" };

const PRIMARY_BUTTON = "h-8 text-xs bg-brand hover:bg-brand-hover text-white";

function startCalendarConnect(kind: CalendarKind) {
  window.location.assign(connectCalendarUrl(kind));
}

function GoogleMark() {
  return (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 23 23" aria-hidden="true">
      <path d="M0 0h10.5v10.5H0V0z" fill="#F25022" />
      <path d="M12.5 0H23v10.5H12.5V0z" fill="#7FBA00" />
      <path d="M0 12.5h10.5V23H0V12.5z" fill="#00A4EF" />
      <path d="M12.5 12.5H23V23H12.5V12.5z" fill="#FFB900" />
    </svg>
  );
}

function providerTestId(kind: CalendarKind | undefined, name: string): string {
  if (kind === "GOOGLE") return "calendar-connect";
  if (kind) return `calendar-connect-${kind}`;
  return `calendar-connect-${name.replace(/\s+/g, "-").toLowerCase()}`;
}

function CalendarProviderPicker() {
  return (
    <div className="space-y-2 max-w-sm">
      {CALENDAR_PROVIDERS.map((provider) => (
        <Button
          key={provider.kind ?? provider.name}
          variant="outline"
          size="sm"
          className="w-full h-10 justify-start text-xs font-medium bg-white hover:bg-gray-50 text-gray-800 disabled:opacity-100 disabled:cursor-default"
          disabled={!provider.available}
          onClick={() => {
            if (!provider.available || !provider.kind) return;
            startCalendarConnect(provider.kind);
          }}
          data-testid={providerTestId(provider.kind, provider.name)}
        >
          {provider.kind === "GOOGLE" ? (
            <GoogleMark />
          ) : provider.kind === "OUTLOOK" ? (
            <MicrosoftMark />
          ) : (
            <CalendarDays className="h-4 w-4" />
          )}
          Connect {provider.name}
          {!provider.available && (
            <Badge variant="secondary" className="ml-auto text-[10px]">Coming soon</Badge>
          )}
        </Button>
      ))}
    </div>
  );
}

function NotConnectedCard() {
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="text-sm">Not connected</CardTitle>
        <CardDescription className="text-xs">
          Choose a calendar provider. You will be asked to allow calendar access,
          then sent back here.
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4">
        <CalendarProviderPicker />
      </CardContent>
    </Card>
  );
}

interface ReconnectCardProps {
  account: string | null;
  kind: CalendarKind;
  unfinished: boolean;
}

function ReconnectCard({ account, kind, unfinished }: ReconnectCardProps) {
  const providerName = calendarProviderName(kind);
  return (
    <Alert className="border-amber-200 bg-amber-50 text-amber-900">
      <AlertTriangle className="h-4 w-4 text-amber-700" />
      <AlertTitle className="text-sm">
        {unfinished ? "Connection was not finished" : "This connection needs to be reconnected"}
      </AlertTitle>
      <AlertDescription className="text-xs space-y-2">
        <p>
          {unfinished
            ? `Connecting started but ${providerName} never sent you back here, so nothing is saved yet. Start again.`
            : `${providerName} no longer accepts the saved access${account ? ` for ${account}` : ""}. ` +
              "Booking is paused until someone reconnects. Providers keep their calendar choice."}
        </p>
        <Button
          size="sm"
          className={PRIMARY_BUTTON}
          onClick={() => startCalendarConnect(kind)}
          data-testid="calendar-reconnect"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Reconnect
        </Button>
      </AlertDescription>
    </Alert>
  );
}

interface ConnectedCardProps {
  connection: CalendarConnection;
  onDisconnected: () => void;
}

function ConnectedCard({ connection, onDisconnected }: ConnectedCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const providerName = calendarProviderName(connection.kind);

  const disconnect = async () => {
    setBusy(true);
    try {
      await disconnectCalendar();
      toast.success(`${providerName} disconnected`);
      setConfirmOpen(false);
      onDisconnected();
    } catch {
      toast.error("Could not disconnect. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <span>{connection.external_account ?? providerName}</span>
          <Badge
            variant="outline"
            className="text-[10px] bg-green-100 text-green-800 border-green-200 hover:bg-green-100"
            data-testid="calendar-status"
          >
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        </CardTitle>
        <CardDescription className="text-xs">
          The assistant reads free/busy time and writes appointments into
          calendars in this account.
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4">
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs text-red-700 border-red-200 hover:bg-red-50"
          onClick={() => setConfirmOpen(true)}
          data-testid="calendar-disconnect"
        >
          <Unplug className="h-3.5 w-3.5 mr-1.5" />
          Disconnect
        </Button>
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Disconnect {providerName}?</DialogTitle>
              <DialogDescription>
                The assistant stops booking for every provider until a calendar
                is connected again. Providers keep their calendar choice, so
                reconnecting the same account picks up where you left off.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmOpen(false)}
                disabled={busy}
              >
                Keep connected
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={disconnect}
                disabled={busy}
                data-testid="calendar-disconnect-confirm"
              >
                {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Disconnect
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

interface CalendarsCardProps {
  state: CalendarsState;
  providerName: string;
  onRefresh: () => void;
}

function CalendarsCard({ state, providerName, onRefresh }: CalendarsCardProps) {
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm">Calendars in this account</CardTitle>
            <CardDescription className="text-xs mt-1">
              Create or share calendars in {providerName}, then refresh.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs shrink-0"
            onClick={onRefresh}
            disabled={state.kind === "loading"}
            data-testid="calendars-refresh"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 space-y-2">
        {state.kind === "loading" && (
          <p className="text-xs text-gray-500 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading calendars…
          </p>
        )}
        {state.kind === "error" && (
          <p className="text-xs text-red-700">
            Could not load calendars. Refresh to try again.
          </p>
        )}
        {state.kind === "ok" && state.calendars.length === 0 && (
          <p className="text-xs text-gray-500" data-testid="calendars-empty">
            No calendars you can write to. Create one in {providerName}, then
            refresh.
          </p>
        )}
        {state.kind === "ok" && state.calendars.length > 0 && (
          <ul className="divide-y" data-testid="calendars-list">
            {state.calendars.map((calendar) => (
              <li
                key={calendar.id}
                className="flex items-center justify-between gap-2 py-1.5 text-xs"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-gray-800 truncate">{calendar.summary}</span>
                  {calendar.primary && (
                    <Badge variant="secondary" className="text-[10px]">
                      Primary
                    </Badge>
                  )}
                </span>
                <span className="flex items-center gap-2 text-gray-500 shrink-0">
                  {calendar.time_zone && <span>{calendar.time_zone}</span>}
                  {calendar.access_role && (
                    <Badge variant="outline" className="text-[10px]">
                      {calendar.access_role}
                    </Badge>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-gray-500 pt-2 border-t">
          One calendar per provider is recommended.
        </p>
      </CardContent>
    </Card>
  );
}

interface CalendarConnectionSectionProps {
  connection: CalendarConnection | null | undefined;
  calendars: CalendarsState;
  loadFailed: boolean;
  needsReconnect: boolean;
  onRetry: () => void;
  onRefreshCalendars: () => void;
  onDisconnected: () => void;
}

function CalendarConnectionSection({
  connection,
  calendars,
  loadFailed,
  needsReconnect,
  onRetry,
  onRefreshCalendars,
  onDisconnected,
}: CalendarConnectionSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthToastShown = useRef(false);

  useEffect(() => {
    if (oauthToastShown.current) return;
    if (searchParams.get("calendar_connected") === "true") {
      toast.success("Calendar connected");
    } else if (searchParams.get("calendar_error") === "true") {
      toast.error("Connecting did not finish. Try again.");
    } else {
      return;
    }
    oauthToastShown.current = true;
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  if (loadFailed) {
    return (
      <div className="flex items-center gap-3 text-xs text-red-700" data-testid="connection-load-error">
        Could not load the calendar connection.
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onRetry}>
          <RotateCcw className="h-3 w-3 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  if (connection === undefined) {
    return (
      <p className="text-xs text-gray-500 flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading calendar…
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {(connection === null || connection.status === "DISCONNECTED") && (
        <NotConnectedCard />
      )}
      {needsReconnect && connection && (
        <ReconnectCard
          account={connection.external_account}
          kind={connection.kind}
          unfinished={connection.status === "PENDING"}
        />
      )}
      {connection?.status === "CONNECTED" && !needsReconnect && (
        <>
          <ConnectedCard connection={connection} onDisconnected={onDisconnected} />
          <CalendarsCard
            state={calendars}
            providerName={calendarProviderName(connection.kind)}
            onRefresh={onRefreshCalendars}
          />
        </>
      )}
    </div>
  );
}

export default CalendarConnectionSection;
